import { Avatar } from 'antd';
import { typography } from '@/design/tokens.js';
import { initials } from './initials.js';

/**
 * The API serves photos itself, so it returns a path rather than an absolute
 * URL — it has no reason to know what hostname it is reachable on. Resolving
 * that path happens here, in the one component every face goes through, so no
 * screen has to remember to do it.
 */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

function resolve(photoUrl: string | null | undefined): string | undefined {
  if (!photoUrl) return undefined;
  return photoUrl.startsWith('/') ? `${BASE_URL}${photoUrl}` : photoUrl;
}

export interface PersonAvatarProps {
  fullName: string;
  photoUrl?: string | null;
  size?: number;
}

/**
 * Somebody's photo, or their initials.
 *
 * Shared rather than re-derived per screen: initials are the fallback on the
 * profile header, every directory card, every org-chart node and the team
 * list, and two screens disagreeing about whether "Aarav Kumar Sharma" is AS
 * or AK is the kind of small inconsistency that makes a product feel
 * unfinished.
 */
export function PersonAvatar({ fullName, photoUrl, size = 40 }: PersonAvatarProps) {
  return (
    <Avatar
      size={size}
      src={resolve(photoUrl)}
      style={{
        backgroundColor: 'var(--ant-color-primary-bg)',
        color: 'var(--ant-color-primary)',
        fontSize: Math.max(11, Math.round(size * 0.36)),
        fontWeight: typography.weight.medium,
        flexShrink: 0,
      }}
    >
      {initials(fullName)}
    </Avatar>
  );
}
